/*
 * DISCLAIMER
 *
 * Wow. Modals are a pain in the arse huh?
 *
 * This file won't solve all your problems, but it will give you full control
 *  over:
 *   A. when modals lock/unlock the screen and
 *   B. when they're automatically closed
 * Remember, it can be VERY IMPORTANT to close modals & friends because they're
 *  locking down scrolling of the screen. For example, there used to be a bug
 *  where if you opened the zoom image on the PDP and then used the back button
 *  to navigate to another page, you couldn't scroll anymore. Make sense? It's
 *  because the zoom image was never closed, and thus the screen never unlocked.
 *
 * With great power comes great responsibility. Read the comments in this file,
 *  and understand the logic flows you're working with.
 *
 * PRACTICAL INSTRUCTIONS
 *
 * To make the screen lock or unlock for a particular action:
 *   1. Add the action to the switch in `getKey` and define a key string for it
 *   2. Add the action to the list in `addLockSaga` or `removeLockSaga`
 *
 * To make a modal automatically close (and then unlock the screen) on a page
 *  change:
 *   1. `yield put` that action in the `closeModalsWorker`
 */

import {put, select, takeEvery, takeLatest} from 'redux-saga/effects'
import {
  CLOSE_ONBOARDING,
  CLOSED_MADLIBS_ONBOARDING,
  OPEN_ONBOARDING,
  OPENED_MADLIBS_ONBOARDING,
} from '../components/Onboarding/redux/actions'
import * as lock from '../lock'
import * as pane from '../pane'

import {CLOSE_DASHBOARD, OPEN_DASHBOARD} from '../actions/dashboard'
import {ENTER_FIT_FLOW, LEAVE_FIT_FLOW} from '../actions/fit'
import {CLOSE_MOBILE_MENU, OPEN_MOBILE_MENU} from '../actions/mobileMenu'
import {CLOSE_SLIDING_MODAL, OPEN_SLIDING_MODAL, closeSlidingModal} from '../actions/modal'
import {
  CLOSE_FILTER_MODAL,
  OPEN_FILTER_MODAL,
  closeFilterModal,
} from '../actions/productCategoryPage'
import {
  CLOSE_PARTY_AVAILABLE_MODAL,
  CLOSE_SOLD_OUT_MODAL,
  CLOSE_ZOOM_MODAL,
  OPEN_PARTY_AVAILABLE_MODAL,
  OPEN_SOLD_OUT_MODAL,
  OPEN_ZOOM_MODAL,
  closePartyAvailableModal,
  closeSoldOutModal,
  closeZoomModal,
} from '../actions/productDetailPage'
import {
  CLOSE_REVIEW_IMAGE_MODAL,
  OPEN_REVIEW_IMAGE_MODAL,
  closeReviewImageModal,
} from '../actions/reviews'
import {ADD_LOCK, REMOVE_LOCK, SUBSEQUENT_PAGE_LOAD} from '../actions/site'
import * as userInteractionActions from '../actions/userInteractions'
import {
  CLOSE_VENDOR_APPLICATION,
  OPEN_VENDOR_APPLICATION,
  closeVendorApplication,
} from '../actions/blackDesk'
import * as site from '../store/site'

// Here are the keys that will identify which UI feature is locking the window,
//  preventing scrolling.

const getKey = (type) => {
  switch (type) {
    case userInteractionActions.CLOSE_CONFIRM_MODAL:
    case userInteractionActions.OPEN_CONFIRM_MODAL:
      return 'confirmation-modal'
    case userInteractionActions.OPEN_MODAL:
    case userInteractionActions.CLOSE_MODAL:
      return 'modal'
    case CLOSE_DASHBOARD:
    case OPEN_DASHBOARD:
      return 'dashboard'
    case CLOSE_FILTER_MODAL:
    case OPEN_FILTER_MODAL:
      return 'category-filter-modal'
    case CLOSE_MOBILE_MENU:
    case OPEN_MOBILE_MENU:
      return 'mobile-menu'
    case CLOSE_ONBOARDING:
    case OPEN_ONBOARDING:
      return 'onboarding'
    case CLOSE_PARTY_AVAILABLE_MODAL:
    case OPEN_PARTY_AVAILABLE_MODAL:
      return 'party-available-modal'
    case CLOSE_REVIEW_IMAGE_MODAL:
    case OPEN_REVIEW_IMAGE_MODAL:
      return 'review-image-modal'
    case CLOSE_SLIDING_MODAL:
    case OPEN_SLIDING_MODAL:
      return 'sliding-modal'
    case CLOSE_SOLD_OUT_MODAL:
    case OPEN_SOLD_OUT_MODAL:
      return 'sold-out-modal'
    case CLOSE_ZOOM_MODAL:
    case OPEN_ZOOM_MODAL:
      return 'zoom-image-modal'
    case ENTER_FIT_FLOW:
    case LEAVE_FIT_FLOW:
      return 'fit-flow'
    case CLOSE_VENDOR_APPLICATION:
    case OPEN_VENDOR_APPLICATION:
      return 'black-desk'
    case CLOSED_MADLIBS_ONBOARDING:
    case OPENED_MADLIBS_ONBOARDING:
      return 'madlibs-onboarding'
    default:
      console.error(`Could not match action type '${type}' to key.`)
      return undefined
  }
}

// ----
// Lock
// ----

// If the `lockWindow` property in the site store contains one or more items,
//  make sure the window is locked. Otherwise, make sure it's unlocked.

export function* lockWorker({payload: {x, y}}) {
  const state = yield select()
  if (site.windowIsLocked(state)) {
    lock.lock(x, y)
  } else {
    lock.unlock(x, y)
  }
}

// React to every addition or removal of an item.

export function* lockSaga() {
  yield takeEvery([ADD_LOCK, REMOVE_LOCK], lockWorker)
}

// -- Add Lock

export function* addLockWorker({type}) {
  const key = getKey(type)

  if (key) {
    // In the case of ENTER_FIT_FLOW, the window is locking at the same time the
    //  page changes, resulting in an old scroll position on a new page. This
    //  causes the Fit Flow content to be transformed up and out of view if we
    //  don't intervene.
    const scrollToTop = [ENTER_FIT_FLOW].includes(type)

    yield put({
      type: ADD_LOCK,
      payload: {
        key,
        x: scrollToTop ? 0 : pane.scrollX(),
        y: scrollToTop ? 0 : pane.scrollY(),
      },
    })
  }
}

// This is a list of all actions that we would like to lock the window.

export function* addLockSaga() {
  // add actions here and in `getKey()`
  yield takeEvery(
    [
      ENTER_FIT_FLOW,
      userInteractionActions.OPEN_CONFIRM_MODAL,
      userInteractionActions.OPEN_MODAL,
      OPEN_DASHBOARD,
      OPEN_FILTER_MODAL,
      OPEN_MOBILE_MENU,
      OPEN_ONBOARDING,
      OPENED_MADLIBS_ONBOARDING,
      OPEN_PARTY_AVAILABLE_MODAL,
      OPEN_REVIEW_IMAGE_MODAL,
      OPEN_SLIDING_MODAL,
      OPEN_SOLD_OUT_MODAL,
      OPEN_VENDOR_APPLICATION,
      OPEN_ZOOM_MODAL,
    ],
    addLockWorker,
  )
}

// -- Remove Lock

export function* removeLockWorker({type}) {
  const key = getKey(type)

  yield put({
    type: REMOVE_LOCK,
    payload: {
      key,
      x: pane.scrollX(),
      y: pane.scrollY(),
    },
  })
}

// This is a counter-list of the above items.
// ✓ for everything that's automatically triggered on a route change. see
//  `closeModalsWorker` below

export function* removeLockSaga() {
  // add actions here and in `getKey()`
  yield takeEvery(
    [
      LEAVE_FIT_FLOW,
      userInteractionActions.CLOSE_CONFIRM_MODAL, // ✓
      userInteractionActions.CLOSE_MODAL, // ✓
      CLOSE_DASHBOARD, // ✓ (in `subsequentPageLoadWorker`)
      CLOSE_FILTER_MODAL, // ✓
      CLOSE_MOBILE_MENU, // ✓ (in `subsequentPageLoadWorker`)
      CLOSE_ONBOARDING,
      CLOSED_MADLIBS_ONBOARDING,
      CLOSE_PARTY_AVAILABLE_MODAL, // ✓
      CLOSE_REVIEW_IMAGE_MODAL, // ✓
      CLOSE_SLIDING_MODAL, // ✓
      CLOSE_SOLD_OUT_MODAL, // ✓
      CLOSE_VENDOR_APPLICATION, // ✓
      CLOSE_ZOOM_MODAL, // ✓
    ],
    removeLockWorker,
  )
}

// ------------
// Close Modals
// ------------

function* closeModalsWorker() {
  yield put(userInteractionActions.closeModal())
  yield put(userInteractionActions.closeConfirmModal()) // My Events
  yield put(closeFilterModal()) // PCP
  yield put(closePartyAvailableModal()) // PDP
  yield put(closeSoldOutModal()) // PDP
  yield put(closeZoomModal()) // PDP
  yield put(closeReviewImageModal()) // Reviews
  yield put(closeVendorApplication()) // Black Desk
  yield put(closeSlidingModal()) // LookNamePanel, Dashboard ParticipantForm
}

export function* closeModalsSaga() {
  yield takeLatest(SUBSEQUENT_PAGE_LOAD, closeModalsWorker)
}
